/**
 * `<ScorecardGrid>` — Interview Scorecard table (B-13, rebuilt per prototype canon).
 *
 * Matches prototype `renderScorecard` (L601-618) + CSS (L350-359).
 *
 * Prototype CSS canon:
 *   .scorecard { background:var(--card); border:1px solid var(--bdr); border-radius:10px; overflow:hidden; }
 *   .sc-hdr { grid-template-columns:1.2fr 2.5fr 90px; padding:8px 14px; background:var(--surf2);
 *             border-bottom:1px solid var(--bdr); font-size:9px; font-weight:700;
 *             text-transform:uppercase; letter-spacing:.9px; color:var(--txt3); }
 *   .sc-row { grid-template-columns:1.2fr 2.5fr 90px; padding:9px 14px;
 *             border-bottom:1px solid var(--bdr); align-items:center; }
 *   .sc-row:last-child { border-bottom:none; }
 *   .sc-row:hover { background:rgba(255,255,255,.02); }
 *   .sc-col-vrd { display:flex; flex-direction:column; align-items:flex-end; gap:3px; }
 *   .sc-stars { font-size:13px; letter-spacing:1px; line-height:1; }
 *   .sc-vrd { font-size:9px; font-weight:700; padding:1px 6px; border-radius:20px; border:1px solid; }
 *
 * B-13 BLOCKERs fixed:
 *   - Grid: `grid-template-columns: 1.2fr 2.5fr 90px` (was `1fr 1.5fr auto`)
 *   - Header band: --surf2 background (was plain bg)
 *   - Container: `.scorecard` bordered card (bg/border/radius/overflow)
 *   - Row hover: `rgba(255,255,255,.02)`
 *   - Rating cell: `flex-col items-end gap-[3px]` — stars OVER pill, right-aligned
 *   - Verdict pill: `border-radius:20px` (fully rounded, not `rounded` 4px)
 *
 * Wraps in `<CollapsibleCard title="Interview Scorecard" defaultOpen={true}>`.
 * Uses `-mx-3.5 -mb-3` to break out of CollapsibleCard's default px/py padding
 * so the grid edges align with the card edges.
 */
import type { JSX } from "react";
import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import {
  type ScorecardRow,
  VERDICT_COLORS,
  VERDICT_STARS,
} from "@/lib/interview-analysis/parsers";
import { VERDICT_NULL_COLOR, NAV_HOVER_BG_02 } from "@/lib/tokens";

interface ScorecardGridProps {
  rows: ScorecardRow[];
}

function StarRating({ verdict }: { verdict: string }): JSX.Element {
  const count = VERDICT_STARS[verdict] ?? 3;
  const color = VERDICT_COLORS[verdict] ?? VERDICT_NULL_COLOR;
  return (
    <span
      data-testid="scorecard-stars"
      aria-label={`${count} out of 5 stars`}
      style={{ fontSize: "13px", letterSpacing: "1px", lineHeight: "1" }}
    >
      <span style={{ color }}>{"★".repeat(count)}</span>
      <span style={{ color: "var(--text-tertiary)", opacity: 0.4 }}>
        {"☆".repeat(5 - count)}
      </span>
    </span>
  );
}

export function ScorecardGrid({ rows }: ScorecardGridProps): JSX.Element | null {
  if (!rows.length) return null;

  return (
    <CollapsibleCard title="Interview Scorecard" defaultOpen={true}>
      {/*
       * Break out of CollapsibleCard's default px/py padding so grid
       * edges align with the card border edges (prototype: zero padding
       * on the scorecard container, padding is per-row via .sc-row).
       */}
      <div className="-mx-3.5 -mb-3" data-testid="scorecard-grid">
        {/*
         * .scorecard container: bg:var(--card); border:1px var(--bdr); radius:10px; overflow:hidden
         * Already provided by CollapsibleCard wrapper.
         * Inner .sc-hdr — surf2 background, 9px uppercase --txt3 labels.
         */}

        {/* Header row — prototype .sc-hdr */}
        <div
          className="grid border-b border-border-default"
          style={{
            gridTemplateColumns: "1.2fr 2.5fr 90px",
            gap: "0 16px",
            padding: "8px 14px",
            background: "var(--surface-subtle)", // --surf2
          }}
          data-testid="scorecard-header"
        >
          <span
            className="text-text-tertiary"
            style={{ fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".9px" }}
          >
            Dimension
          </span>
          <span
            className="text-text-tertiary"
            style={{ fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".9px" }}
          >
            Signal
          </span>
          <span
            className="text-right text-text-tertiary"
            style={{ fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".9px" }}
          >
            Rating
          </span>
        </div>

        {/* Data rows — prototype .sc-row */}
        {rows.map((row, i) => {
          const color = VERDICT_COLORS[row.verdict] ?? VERDICT_NULL_COLOR;
          return (
            <div
              key={i}
              className="grid border-border-default last:border-b-0"
              style={{
                gridTemplateColumns: "1.2fr 2.5fr 90px",
                gap: "0 16px",
                padding: "9px 14px",
                borderBottom: "1px solid var(--border-default)",
                alignItems: "center",
              }}
              data-testid="scorecard-row"
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = NAV_HOVER_BG_02;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "";
              }}
            >
              {/* .sc-dim */}
              <span
                style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}
              >
                {row.dim}
              </span>
              {/* .sc-sig */}
              <span
                style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: "1.4" }}
              >
                {row.signal}
              </span>
              {/* .sc-col-vrd — flex-col items-end gap-[3px]: stars OVER pill, right-aligned */}
              <span
                className="flex flex-col items-end"
                style={{ gap: "3px" }}
                data-testid="scorecard-rating-cell"
              >
                <StarRating verdict={row.verdict} />
                <span
                  data-testid="scorecard-verdict-pill"
                  data-verdict={row.verdict}
                  style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: "20px",
                    border: "1px solid",
                    whiteSpace: "nowrap",
                    color,
                    borderColor: `${color}44`,
                    background: `${color}14`,
                  }}
                >
                  {row.verdict}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </CollapsibleCard>
  );
}
